<template>
  <div class="multiple-choice-type">
    <SlideTitleSettingsV2 :keyUndoRedo="key" :showImageInputField="inputQuestionImageVisible" />
    <SlideHints />
    <AdditionalFields v-if="isTypeHasAdditionalFields" />
    <SubheadingInput v-if="isContentSlide" :key="'text_slide' + key" @updateSlide="updateSlide" />
    <WordCloudInput
      v-if="slideActive.type == 'wordCloud'"
      @updateSettingSlide="updateSettingSlide"
    />
    <SlideOptionAutoCompleteToggle
      v-if="canUseNewAutocomplete "
      @addOption="addOption"
      @updateOption="_updateOption"
      @updateTitleOption="updateTitleOption"
    />
    <GenerateOptionsButton v-if="canUseGenerateOptions" />

    <SlideOptionsInputV2
      :isSortingSlideOptions="isSortingSlideOptions"
      :optionLabel="optionLabel"
      :optionTooltip="optionTooltip"
      :keyInput="key"
      :optionPlaceHolder="optionPlaceHolder"
      @sortOption="sortOption"
      @updateSettingSlide="updateSettingSlide"
      @updateTitleOption="updateTitleOption"
      @addOption="addOption"
      @deleteOption="deleteOption"
      @updateOption="_updateOption"
      @backupAddOptionFromTab="backupAddOptionFromTab"
    />

    <!-- End of slide options -->
    <RightColumnQuizSettings
      v-if="slideActive.type === 'pickAnswer'"
      :slideType="slideActive.slideType"
      :multipleChoice="slideActive.multipleChoice"
    />
    <ScaleQuestionContentTab v-if="slideActive.type == 'scale'" @updateSlide="updateSettingSlide" />

    <TimeLimitConfig />

    <OtherSettings v-if="otherSettingsVisible" />
    <!-- Q&A -->
    <RightColumnQnASettings v-if="slideActive.type === 'question'" />

    <ModalDeleteScalesStatement
      v-if="isModalDeleteScalesStatementVisible"
      :modalContent="contentModalDeleteScalesStatement"
      :deleteSlideOptionIndex="deleteSlideOptionIndex"
      @deleteOption="handleDeleteScalesStatement"
      @hide="handleCloseModalDeleteScalesStatement()"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import * as Sentry from '@sentry/vue'
import Config from '@/config'
import AdditionalFields from '@/components/presentations/AdditionalFields'
import Utils from '@/util'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import TimeLimitConfig from '@/components/presentations/types/TimeLimitConfig.vue'
import ScaleQuestionContentTab from '@/components/presentations/types/ScaleQuestionContentTab.vue'
import RightColumnQuizSettings from '@/components-v2/presentation-editor/side-right/RightColumnQuizSettings.vue'
import SubheadingInput from '@/components-v2/presentation-editor/side-right/SubheadingInput.vue'
import WordCloudInput from '@/components-v2/presentation-editor/side-right/WordCloudInput.vue'
import SlideOptionsInputV2 from '@/components-v2/presentation-editor/side-right/SlideOptionsInputV2.vue'
import SlideHints from '@/components-v2/presentation-editor/side-right/hints/SlideHints.vue'
import RightColumnQnASettings from '@/components-v2/presentation-editor/side-right/RightColumnQnASettings.vue'
import OtherSettings from '@/components-v2/presentation-editor/side-right/OtherSettings.vue'
import SlideTitleSettingsV2 from '@/components-v2/presentation-editor/side-right/SlideTitleSettingsV2.vue'
import PresentationUtils from '@/utils/presentation'

// Generate answers components
import SlideOptionAutoCompleteToggle from '@/components-v2/presentation-editor/side-right/SlideOptionAutoCompleteToggle.vue'
import GenerateOptionsButton from '@/components-v2/presentation-editor/side-right/generate-options/GenerateOptionsButton.vue'
// modals
import ModalDeleteScalesStatement from '@/components-v2/modals/DeleteScalesStatement.vue'

// api
import SlideOptionApi from '@/api/slideOption'
import eventCategories from '@/constant/eventCategories'
import { ALLOW_PICKING_MORE_OPTION } from '@/constant/eventActions'
import constants from '@/constant'
import SlideApi from '@/api/slide'

// services
import cookie from '@/services/cookie'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import UpdateTitleSlideMixin from '@/mixins/undo-redo/UpdateTitleSlideMixin'

export default {
  name: 'MultipleChoiceQuestion',
  components: {
    AdditionalFields,
    TimeLimitConfig,
    ScaleQuestionContentTab,
    RightColumnQuizSettings,
    ModalDeleteScalesStatement,
    SubheadingInput,
    WordCloudInput,
    SlideOptionsInputV2,
    SlideHints,
    RightColumnQnASettings,
    OtherSettings,
    SlideTitleSettingsV2,

    // Generate answers components
    SlideOptionAutoCompleteToggle,
    GenerateOptionsButton,
  },
  mixins: [UndoRedoMixin, UpdateTitleSlideMixin],
  data() {
    return {
      isModalDeleteScalesStatementVisible: false,
      deleteSlideOptionIndex: null,
      optionLabel: 'Options',
      optionTooltip: '',
      optionPlaceHolder: 'Option',
      descIsOpen: false,
      showQuizInfoTop: true,
      isSortingSlideOptions: false,
      key: null,
      mappingUpdateOptionData: {},
    }
  },
  computed: {
    ...mapGetters(['isPresenting']),
    ...mapGetters('slideRenderHandler', ['inputQuestionImageVisible']),
    ...mapGetters('featureToggles', [
      'canUseNewAutocomplete',
      'canUseGenerateOptions',
    ]),
    ...mapState({
      slideActive: (state) => state.slideActive,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
      isPresenting: (state) => state.isPresenting,
    }),

    notAllowedDeleteOptions() {
      if (this.slideActive.type === 'multipleChoice'
        || this.slideActive.type === 'pickAnswer'
        || this.slideActive.type === 'imageChoice') {
        return this.slideActive.SlideOptions.length === 2
      }
      if (this.slideActive.type === 'scale') {
        return this.slideActive.SlideOptions.length === 1
      }
      return false
    },
    isSlideTitleSettingVisible() {
      const { type } = this.$store.state.slideActive
      return type === 'scale' ||
        type === 'multipleChoice' ||
        type === 'openEnded' ||
        type === 'bulletPoints' ||
        type === 'textSlide' ||
        type === 'voiceQA' ||
        type === 'wordCloud' ||
        type === 'pickAnswer' ||
        type === 'leaderboard' ||
        type === 'imageChoice' ||
        type === 'question' ||
        type === 'ideas'
    },
    isShowQuizInfo() {
      const { type } = this.$store.state.slideActive
      return this.showQuizInfoTop && (type === 'pickAnswer' || type === 'leaderboard')
    },
    isContentSlide() {
      const { type } = this.$store.state.slideActive
      return type === 'textSlide' || type === 'bulletPoints'
    },
    isTypeHasAdditionalFields() {
      const slideTypes = ['openEnded', 'ideas']
      return slideTypes.includes(this.slideActive.type)
    },
    otherSettingsVisible() {
      const slideTypes = ['scale', 'imageChoice', 'multipleChoice', 'openEnded', 'bulletPoints',
        'wordCloud', 'pickAnswer', 'leaderboard', 'question', 'ideas']
      return slideTypes.includes(this.slideActive.type)
    },
    isEURegion() {
      return Config.region === constants.euRegion
    },
    isSlideQuiz() {
      return this.slideActive.type === 'pickAnswer'
    },
    contentModalDeleteScalesStatement() {
      return 'Deleting this statement will erase all audience responses on this slide.'
    },
  },
  watch: {

    '$store.state.presentation.fontFamily': function () {
      const el = this.$refs['form-check-hide-results']
      if (!el) return
      if (
        this.$store.state.presentation.fontFamily ==
        "'Source Code Pro', 'Courier New', Courier, monospace"
      ) {
        el.classList.add('form-check-font-scpro')
      } else {
        el.classList.remove('form-check-font-scpro')
      }
    },
  },
  mounted() {
    this.registerHistory()
    const slideType = this.slideActive.type
    if (slideType === 'multipleChoice' || slideType === 'imageChoice') {
      this.optionLabel = this.$i18nPresenter.t('Options')
      this.optionTooltip = this.$i18nPresenter.t('Enter the options you want your audience to vote on (max 30)')
    } else if (slideType === 'bulletPoints') {
      this.optionLabel = this.$i18nPresenter.t('Bullet points')
      this.optionTooltip = this.$i18nPresenter.t('Enter your bullet points (up to 15)')
      this.optionPlaceHolder = 'Bullet'
    } else if (slideType === 'voiceQA') {
      this.optionLabel = this.$i18nPresenter.t('Participant info')
    } else if (slideType === 'pickAnswer') {
      this.optionTooltip = this.$i18nPresenter.t('Write the answer options you want players to choose from.')
      this.optionLabel = this.$i18nPresenter.t('Options')
      this.pickmoreTooltip = this.$i18nPresenter.t('If this is unselected, players can get full points by picking just one correct answer')

      if (this.slideActive.slideType === 'matchPairs') {
        this.optionTooltip = this.$i18nPresenter.t('Enter the options you want your audience to choose from (max 4)')
        this.optionLabel = this.$i18nPresenter.t('Pairs')
      }
      if (this.slideActive.slideType === 'correctOrder') {
        this.optionLabel = this.$i18nPresenter.t('Statements')
        this.optionTooltip = this.$i18nPresenter.t('Enter your statements. They will be placed in random order on the slide.')
      }
    } else if (slideType === 'scale') {
      this.optionLabel = this.$i18nPresenter.t('Statements')
      this.optionTooltip = this.$i18nPresenter.t('Enter the statements for your audience to rate')
      this.optionPlaceHolder = this.$i18nPresenter.t('Statement')
    }
    if (this.slideActive.titleDesc) {
      if (
        slideType === 'multipleChoice' ||
        slideType === 'openEnded' ||
        slideType === 'wordCloud' ||
        slideType === 'imageChoice' ||
        slideType === 'question' ||
        slideType === 'scale' ||
        slideType === 'pickAnswer'
      ) {
        this.descIsOpen = true
      }
    }
    const showQuizInfoTop = cookie.getValue('showQuizInfoTop')
    if (!showQuizInfoTop) {
      cookie.setValue('showQuizInfoTop', new Date().getTime().toString())
    } else if (
      new Date().getTime() - parseInt(showQuizInfoTop, 10) >
      14 * 24 * 60 * 60 * 1000
    ) {
      this.showQuizInfoTop = false
    }

    const el = this.$refs['form-check-hide-results']
    if (!el) return
    if (
      this.$store.state.presentation.fontFamily ==
      "'Source Code Pro', 'Courier New', Courier, monospace"
    ) {
      el.classList.add('form-check-font-scpro')
    } else {
      el.classList.remove('form-check-font-scpro')
    }
  },

  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSlideActiveAction', 'setSyncLoadingAction', 'setSyncPresentationAction']),
    ...mapGetters(['getSlideActive', 'getSlideOptionsActive']),

    backupAddOptionFromTab() {
      const oldOptions = JSON.parse(JSON.stringify(this.getSlideOptionsActive()))
      oldOptions.pop()
      const newSlideOptions = JSON.parse(JSON.stringify(oldOptions))
      newSlideOptions.push({ index: oldOptions.length + 1, randomIndexId: Utils.generateRandomString() })
      this.addActionToHistory({
        type: 'AddOption',
        data: oldOptions,
        newData: newSlideOptions,
        slideActiveId: this.slideActive.id,
      })
    },

    handleCloseModalDeleteScalesStatement() {
      this.isModalDeleteScalesStatementVisible = false
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Right column > Content Tab > Confirm remove statement > Cancel',
      )
    },

    backupSortOption(newEvent) {
      const eventBackup = {
        moved: {
          element: { id: newEvent.moved.element.id },
          newIndex: newEvent.moved.oldIndex,
          oldIndex: newEvent.moved.newIndex,
        },
      }
      this.addActionToHistory({
        type: 'SortOption',
        data: eventBackup,
        newData: newEvent,
        slideActiveId: this.slideActive.id,
      })
    },

    async restoreSortOption(data) {
      const { newIndex, oldIndex } = data.moved
      await this.swapSlideOptions(newIndex, oldIndex)
      await this.sortOption(data, false)
      this.key = (new Date()).getTime()
    },

    async swapSlideOptions(newIndex, oldIndex) {
      const slideOptions = this.getSlideOptionsActive()

      const temp = slideOptions[oldIndex]
      slideOptions.splice(oldIndex, 1)
      slideOptions.splice(newIndex, 0, temp)

      await this.updateSlideActiveAction({
        SlideOptions: JSON.parse(JSON.stringify(slideOptions)),
      })
    },

    async sortOption(event, isBackup = true) {
      if (isBackup) {
        this.backupSortOption(event)
      }
      const slideOptions = this.getSlideOptionsActive()

      if (slideOptions.length < 2 || !event || !event.moved) return

      const { newIndex, oldIndex, element: movedSlideOption } = event.moved

      if (newIndex === oldIndex) return
      if (this.isSortingSlideOptions) return

      this.isSortingSlideOptions = true
      let newOrder = null
      if (newIndex === slideOptions.length - 1) {
        newOrder = slideOptions[slideOptions.length - 2].order + 1
      } else if (newIndex === 0) {
        newOrder = slideOptions[1].order / 2
      } else {
        newOrder = (slideOptions[newIndex + 1].order + slideOptions[newIndex - 1].order) / 2
      }

      const newSlideOptions = slideOptions.map((option) => {
        if (option.id === movedSlideOption.id) {
          return { ...option, order: newOrder }
        }

        return option
      })

      this.updateSlideActiveAction({
        SlideOptions: newSlideOptions,
      })
      EventBus.$emit('slide-option-input-update-value')

      if (!movedSlideOption.id) {
        this.isSortingSlideOptions = false
      }

      if (movedSlideOption.id) {
        this.$store.dispatch('setSyncPresentation', 1)
        try {
          await SlideOptionApi.patchSlideOptionById(movedSlideOption.id, {
            order: newOrder,
            presentationId: this.$store.state.presentation.id,
            uniqueAccessCode: this.$store.state.presentation.uniqueAccessCode,
            socketId: Socket.getSocketId(),
          })

          this.$store.dispatch('setSyncPresentation', 2)
          this.isSortingSlideOptions = false
        } catch (error) {
          this.$store.dispatch('setSyncPresentation', 3)
          this.isSortingSlideOptions = false
          throw error
        }
      }

      EventBus.$emit('changeSlideOption')
    },

    backupUpdateOption(oldData, newData, key) {
      this.addActionToHistory({
        type: 'InputTitleOption',
        data: oldData,
        newData,
        slideActiveId: this.slideActive.id,
      }, key)
    },

    async restoreInputTitleOption(data) {
      const dataBackup = {
        ...data,
        value: data.value ? data.value : '',
      }
      if (dataBackup.id) {
        const oldSlideOptions = this.getSlideOptionsActive()
        const findItem = oldSlideOptions.find((item) => item.id === dataBackup.id)
        if (!findItem) {
          await this.callAPIEventCreate(dataBackup)
        }
      }
      await this.updateTitleOption(dataBackup, false)
      const SlideOptions = this.getSlideOptionsActive()
      this.updateSlideActiveAction({
        SlideOptions: [],
      })
      this.key = (new Date()).getTime()
      await new Promise((resolve) => {
        this.$nextTick(() => {
          this.updateSlideActiveAction({
            SlideOptions,
          })
          this.key = (new Date()).getTime()
          resolve()
        })
      })
    },

    getOptionFromEventData(eventData) {
      const slideOptions = this.getSlideOptionsActive()
      if (eventData.id) {
        return slideOptions.find((item) => item.id === eventData.id)
      }
      return slideOptions[eventData.index]
    },

    async updateTitleOption(data, isBackup = true) {
      const key = (new Date()).getTime()
      this.lockHistory(key)
      let currentTitle = ''
      const option = this.getOptionFromEventData(data)
      if (option) {
        currentTitle = option.title
      }
      const id = await this.updateOption(data)
      if (typeof id !== 'number') {
        this.unlockHistory(key, false)
        return false
      }
      if (!isBackup || currentTitle === data.value) {
        this.unlockHistory(key, false)
        return null
      }
      const oldData = {
        ...data,
        value: currentTitle,
        id,
      }
      const newData = {
        ...data,
        id: oldData.id,
      }
      if (!data.id && currentTitle === '') {
        newData.id = null
      }
      this.backupUpdateOption(oldData, newData, key)
      this.unlockHistory(key)
      return true
    },

    async updateOption(data, cb) {
      this.$store.dispatch('setSyncPresentation', 1)

      if (!data.id) {
        this.mappingUpdateOptionData[data.index] = (data)
      }
      let optionId = data.id
      // create
      if (!data.id) {
        const { SlideOptions: slideOptions } = this.slideActive

        const highestOrder = slideOptions.reduce((accumulator, item) => {
          return item.order > accumulator ? item.order : accumulator
        }, 1)

        if (slideOptions[data.index].isLoading) return null
        slideOptions[data.index].isLoading = true
        const payload = {
          title: data.value,
          order: slideOptions[data.index].order || highestOrder + 1,
          slideId: this.slideActive.id,
          presentationId: this.$store.state.presentation.id,
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          correct: data.correct,
          socketId: Socket.getSocketId(),
          randomIndexId: data.randomIndexId,
        }
        const indexOption = data.index
        try {
          if (data.index === undefined) return null

          if (this.slideActive.SlideOptions[data.index]) {
            slideOptions[data.index].title = payload.title
            slideOptions[data.index].order = payload.order
          }

          this.updateSlideActiveAction({
            SlideOptions: slideOptions,
          })

          const slideId = this.slideActive.id
          const res = await this.$http.post(Config.api.createSlideOption, payload)
          optionId = res.body.id
          if (this.mappingUpdateOptionData[indexOption]) {
            const updatedOption = this.mappingUpdateOptionData[indexOption]
            updatedOption.id = optionId
            this.microUpdateSlideOption(updatedOption)
            delete this.mappingUpdateOptionData[indexOption]
          }
          if (slideOptions[data.index]) {
            slideOptions[data.index].id = res.body.id
          }
          if (slideId === this.slideActive.id) {
            this.updateSlideActiveAction({
              SlideOptions: slideOptions,
            })
          } else {
            const index = this.$store.state.presentation.slides.findIndex((item) => {
              return item.id === this.slideActive.id
            })
            if (index !== -1) {
              this.$store.state.presentation.slides[index].SlideOptions = slideOptions
            }
          }
          const slideOptionLength = slideOptions ? slideOptions.filter((option) => option.id).length : 0
          if (this.slideActive.limitChoice === slideOptionLength - 1) {
            this.slideActive.limitChoice = slideOptionLength
            this.updateSettingSlide(2)
          }

          this.$store.dispatch('setSyncPresentation', 2)
          if (cb) {
            cb(res.body)
          }
          Utils.mixpanelTracking('Presentation Editor', 'Slide option > Edit > Succeeded')
          EventBus.$emit('changeSlideOption')
        } catch (e) {
          Sentry.captureException(e)
          this.$store.dispatch('setSyncPresentation', 3)
        }
      } else {
        await this.microUpdateSlideOption(data)
      }
      return optionId
    },
    async microUpdateSlideOption(data) {
      const { SlideOptions } = this.$store.getters.getSlideActive
      if (SlideOptions.length - 1 < data.index) {
        return
      }
      SlideOptions[data.index].title =
          data.value

      // Redo Add-options sẽ không có id nên phải set lại id
      if (!SlideOptions[data.index].id) {
        SlideOptions[data.index].id = data.id
      }
      this.updateSlideActiveAction({
        SlideOptions,
      })
      await this._updateOption(
        {
          title: data.value,
          deleted: false,
          presentationId: this.$store.state.presentation.id,
          accessCode: this.$store.state.presentation.uniqueAccessCode,
        },
        data.id,
      )
      let typeGa = this.slideActive.type
      if (this.slideActive.type === 'multipleChoice' || this.slideActive.type === 'imageChoice') {
        typeGa = 'Poll'
      }
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Side right > Change slide option title',
        `Slide type: ${typeGa}`,
      )
    },

    async _updateOption(params, id, cb) {
      params.socketId = Socket.getSocketId()
      params.presenting = this.isPresenting
      params.presentationId = this.presentation.id
      const SlideOptions = this.getSlideOptionsActive()
      this.updateSlideActiveAction({
        SlideOptions,
      })

      try {
        await SlideOptionApi.updateSlideOption(id, params)
        this.setSyncPresentationAction(2)
        if (cb) {
          cb()
        }
        EventBus.$emit('changeSlideOption')
        if (!params.title) {
          await this.handleRemoveCheckCorrectOption(id)
        }
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncPresentationAction(3)
      }
    },

    async handleRemoveCheckCorrectOption(id) {
      const slideOptions = this.getSlideOptionsActive()
      const index = slideOptions.findIndex((item) => item.id === id)
      if (index !== -1 && !slideOptions[index].title &&
        !slideOptions[index].image && !slideOptions[index].imageCropped && slideOptions[index].correct) {
        this.$store.state.slideActive.SlideOptions[index].correct = false
        const payload = {
          correct: false,
          accessCode: this.presentation.uniqueAccessCode,
        }
        await this._updateOption(payload, id)
      }
    },

    async updateSlide(data, isBackup = true) {
      if (isBackup) {
        this.backupUpdateSlide(data)
      }
      const payload = {}
      if (data.subheading) {
        payload.subheading = data.subheading
      } else if (data.subheading === '') {
        payload.subheading = ' '
      }

      this.$store.dispatch('setSyncPresentation', 1)
      payload.presentationId = this.$store.state.presentation.id
      payload.accessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      payload.presenting = this.$store.getters.isPresenting || this.$store.getters.isBackstage

      this.updateSlideActiveAction({
        ...payload,
      })

      try {
        await SlideApi.updateSlideById(this.slideActive.id, payload)
        this.$store.dispatch('setSyncPresentation', 2)
      } catch (error) {
        Sentry.captureException(error)
        this.$store.dispatch('setSyncPresentation', 3)
      }
      if (payload.subheading) {
        Utils.mixpanelTracking(
          'Slide',
          'Edit text slide',
          'Done editing subheading',
        )
      }
    },

    // Toggle sticker and GA track
    updateSettingSlide(type, cb = null, paramObj = {}) {
      const params = {
        accessCode: this.$store.state.presentation.uniqueAccessCode,
        socketId: Socket.getSocketId(),
      }
      if (type == 1) {
        if (paramObj.multipleChoice !== undefined) {
          this.$store.state.slideActive.multipleChoice = paramObj.multipleChoice
          params.multipleChoice = paramObj.multipleChoice
        }

        if (paramObj.isCorrectGetPoint !== undefined) {
          this.$store.state.slideActive.isCorrectGetPoint = paramObj.isCorrectGetPoint
          params.isCorrectGetPoint = paramObj.isCorrectGetPoint
        }

        if (this.slideActive.type === 'pickAnswer') {
          params.limitChoice = this.slideActive.SlideOptions.length
        }
      } else if (type == 2) {
        params.limitChoice = this.slideActive.limitChoice
      } else if (type == 4) {
        params.addCorrectOption = this.slideActive.addCorrectOption
      } else if (type == 9) {
        this.slideActive.entriesPerParticipant = parseInt(
          this.slideActive.entriesPerParticipant,
          10,
        )
        if (this.slideActive.entriesPerParticipant < 1) {
          this.slideActive.entriesPerParticipant = 1
        } else if (this.slideActive.entriesPerParticipant > 10) {
          this.slideActive.entriesPerParticipant = 10
        }
        if (!this.slideActive.entriesPerParticipant) {
          params.entriesPerParticipant = 1
          return
        }
        params.entriesPerParticipant = this.slideActive.entriesPerParticipant
      } else if (type == 13) {
        params.isCorrectGetPoint = this.slideActive.isCorrectGetPoint
      } else if (type === 14 || type === 15 || type === 16) {
        params.scale = this.slideActive.scale
      }

      this.setSlideActiveAction(this.getSlideActive())
      params.presenting = this.$store.getters.isPresenting
      this.setSyncLoadingAction(1)
      this._updateSlide(params, type, cb)
    },

    async _updateSlide(params, type, cb) {
      try {
        await SlideApi.updateSlideById(this.slideActive.id, params)
        const index = this.presentation.slides.findIndex(
          (item) => {
            return item.id == this.slideActive.id
          },
        )
        if (index == -1) return
        if (type == 1) {
          this.presentation.slides[
            index
          ].multipleChoice = this.slideActive.multipleChoice
          this.presentation.slides[
            index
          ].isCorrectGetPoint = this.slideActive.isCorrectGetPoint

          const trackData = {
            eventAction: ALLOW_PICKING_MORE_OPTION,
            eventCategory: eventCategories.PRESENTATION_EDITOR,
            eventLabel: `Allow picking more than one option: ${this.slideActive.multipleChoice}`,
            presentationId: this.presentation.id,
            slideType: this.slideActive.type,
            slideId: this.slideActive.id,
          }
          Utils.trackGA4AndMixpanel(ALLOW_PICKING_MORE_OPTION, trackData)
        } else if (type == 2) {
          this._handleAfterUpdateSettingLimitChoice(index)
        } else if (type == 4) {
          this.presentation.slides[
            index
          ].addCorrectOption = this.slideActive.addCorrectOption
          Utils.mixpanelTracking(
            'Slide',
            'Edit setting',
            'Presenter edit setting enable add correct answer',
            this.slideActive.addCorrectOption,
          )
        } else if (type == 9) {
          this.presentation.slides[
            index
          ].entriesPerParticipant = this.slideActive.entriesPerParticipant
          Utils.mixpanelTracking(
            'Slide',
            'Edit setting',
            'Presenter edit setting entriesPerParticipant',
          )
        } else if (type == 13) {
          this.presentation.slides[
            index
          ].isCorrectGetPoint = this.slideActive.isCorrectGetPoint
          if (this.slideActive.isCorrectGetPoint == 'true') {
            Utils.mixpanelTracking(
              'Slide',
              'Edit setting',
              'Presenter edit the more the better',
            )
          } else {
            Utils.mixpanelTracking(
              'Slide',
              'Edit setting',
              'Presenter edit must not miss any',
            )
          }
        }
        this.setSyncLoadingAction(2)
        if (cb) cb()
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
        if (cb) cb(error)
      }
    },

    backupAddOption(slideOptions) {
      const newSlideOptions = JSON.parse(JSON.stringify(slideOptions))
      newSlideOptions.push(
        { index: this.getSlideOptionsActive().length + 1, randomIndexId: Utils.generateRandomString() },
      )
      this.addActionToHistory({
        type: 'AddOption',
        data: JSON.parse(JSON.stringify(slideOptions)),
        newData: JSON.parse(JSON.stringify(newSlideOptions)),
        slideActiveId: this.slideActive.id,
      })
    },

    async undoAddOption() {
      const SlideOptions = this.getSlideOptionsActive()
      this.deleteOption(SlideOptions[SlideOptions.length - 1], SlideOptions.length - 1, false)
      this.key = (new Date()).getTime()
    },

    async redoAddOption() {
      this.addOption(false)
      this.key = (new Date()).getTime()
    },

    addOption(isBackup = true) {
      const { type } = this.slideActive
      const SlideOptions = this.getSlideOptionsActive()
      const optionsLength = SlideOptions.length
      if (optionsLength > 29) return
      if ((type === 'multipleChoice' || type === 'imageChoice') && optionsLength > 29) return
      if (type === 'bulletPoints' && optionsLength > 14) return
      if (type === 'scale' && optionsLength > 7) return

      if (isBackup) {
        this.backupAddOption(JSON.parse(JSON.stringify(SlideOptions)))
      }

      SlideOptions.push({
        index: this.slideActive.SlideOptions.length + 1,
        randomIndexId: Utils.generateRandomString(),
      })
      this.updateSlideActiveAction({
        SlideOptions,
      })
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Side right > Add slide option',
      )
    },

    backupDeleteOption(dataHistory) {
      const { SlideOptions } = this.slideActive
      const dataUndo = {
        item: dataHistory.item,
        index: dataHistory.index,
        slideOptions: SlideOptions,
      }
      this.addActionToHistory(JSON.parse(JSON.stringify({
        type: 'DeleteOption',
        data: dataUndo,
        newData: dataHistory,
        slideActiveId: this.slideActive.id,
      })))
    },

    async undoDeleteOption({ item, slideOptions }) {
      await this.callAPIUpdateFieldDeletedSlideOption(item.id, false)

      this.updateSlideActiveAction({
        SlideOptions: slideOptions,
      })
      await this.callAPIEventCreate(item)
      this.key = (new Date()).getTime()
    },

    async callAPIEventCreate(data) {
      await this.$http
        .post(Config.api.updateEventSlideOption, {
          action: 'create',
          data,
          presentationId: this.$store.state.presentation.id,
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
          presenting: this.$store.getters.isPresenting,
        })
    },

    async callAPIUpdateFieldDeletedSlideOption(slideOptionId, isDelete) {
      await this.$http
        .put(Config.api.updateSlideOption + slideOptionId, {
          deleted: isDelete,
          presentationId: this.$store.state.presentation.id,
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
          presenting: this.$store.getters.isPresenting,
        })
    },

    async redoDeleteOption({ item, index }) {
      await this.deleteOption(item, index, false)
      this.key = (new Date()).getTime()
    },

    deleteOption(item, index, isBackup = true) {
      if (this.notAllowedDeleteOptions) return
      if (isBackup) {
        this.backupDeleteOption({ item, index })
      }
      if (this.slideActive.type === 'scale' && this.slideActive.hasScaleQuestionAnswer) {
        if (!item.id) {
          const { SlideOptions } = this.slideActive
          SlideOptions.splice(index, 1)
          this.updateSlideActiveAction({
            SlideOptions,
          })
          return
        }
        this.deleteSlideOptionIndex = index
        this.isModalDeleteScalesStatementVisible = true
        return
      }
      this._handleDeleteOption(item, index)
      if (this.slideActive.type === 'scale') {
        EventBus.$emit('changeSlideOption')
      }
    },

    handleDeleteScalesStatement(item, index, cb) {
      const SlideOptions = this.getSlideOptionsActive()

      SlideOptions.splice(index, 1)
      this.updateSlideActiveAction({
        SlideOptions,
      })

      if (!item.id) return

      this.setSyncLoadingAction(1)
      this.$http
        .put(Config.api.updateSlideOption + item.id, {
          deleted: true,
          presentationId: this.$store.state.presentation.id,
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
          presenting: this.$store.getters.isPresenting,
        })
        .then(
          () => {
            this.setSyncLoadingAction(2)
            Utils.mixpanelTracking(
              eventCategories.PRESENTATION_EDITOR,
              'Side right > Delete slide option',
            )
            if (cb) cb()
          },
          () => {
            this.setSyncLoadingAction(3)
          },
        )

      if (!item.count) item.count = 0
      if (!item.count) return
      let multipleCountAnswer = 0
      if (!this.slideActive.multipleCountAnswer) multipleCountAnswer = 0
      multipleCountAnswer = this.slideActive.multipleCountAnswer - item.count

      this.updateSlideActiveAction({
        multipleCountAnswer,
      })
    },

    async _handleDeleteOption(item, index) {
      try {
        const SlideOptions = this.getSlideOptionsActive()

        SlideOptions.splice(index, 1)
        this.updateSlideActiveAction({
          SlideOptions,
        })

        if (!item.id) return
        this.setSyncLoadingAction(1)
        const payload = {
          deleted: true,
          presentationId: this.presentation.id,
          accessCode: this.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
          presenting: this.isPresenting,
        }
        await SlideOptionApi.updateSlideOption(item.id, payload)

        this.setSyncLoadingAction(2)
        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Side right > Delete slide option',
        )
        if (
          this.slideActive.limitChoice >
        this.slideActive.SlideOptions.length
        ) {
          this.updateSlideActiveAction({
            limitChoice: this.slideActive.SlideOptions.length,
          })
          this.updateSettingSlide(2)
        }
        if (!item.count) item.count = 0
        if (!item.count) return
        let multipleCountAnswer = 0
        if (!this.slideActive.multipleCountAnswer) multipleCountAnswer = 0
        multipleCountAnswer = this.slideActive.multipleCountAnswer - item.count
        this.updateSlideActiveAction({
          multipleCountAnswer,
        })
        if (!this.slideActive.SlideOptions.length || multipleCountAnswer < 1) {
          PresentationUtils.checkAndUpdatePresentationHasResults()
        }
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },

    _handleAfterUpdateSettingLimitChoice(index) {
      this.$store.state.presentation.slides[
        index
      ].limitChoice = this.slideActive.limitChoice
      let slideType = this.slideActive.type
      if (slideType === 'ideas') {
        slideType = 'brainstorm'
      }
    },

  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.input-group {
  user-select: none;
}
.form-check-inline::v-deep {
  &.form-check-font-scpro {
    margin-bottom: 40px;
  }
  @media (max-width: 320px) {
    &.hide-result-form {
      margin-right: 0;
      .button-show-all {
        margin-left: -2.5px;
      }
    }
  }
}
.input-option {
  & ::v-deep .input-style {
    width: min-content;
  }
}
</style>
